'use client';

import { c as _c } from "react/compiler-runtime";
import type { VariantProps } from 'class-variance-authority';
import { type RefObject, useEffect, useRef } from 'react';
import { cn } from '@/app/utils/cn';
import { cnva } from '@/app/utils/cnva';
import { createDataAttributes, type ExtendDataKeys } from '@/app/utils/createDataAttributes';
import type { DataGAProps } from '@/app/utils/createDataGA';
import { createDataGA } from '@/app/utils/createDataGA';
import { formatPrice } from '@/app/utils/format';
import { safeCall } from '@/app/utils/safeCall';
const ListSelectionStyled = cnva(['text-body-2 rounded-2xs relative flex w-full cursor-pointer justify-between gap-2 p-3 text-left', 'before:transition-form before:rounded-2xs before:pointer-events-none before:absolute before:inset-0 before:border-2 before:border-transparent focus-visible:outline-none'], {
  variants: {
    theme: {
      light: '',
      dark: ''
    },
    isChecked: {
      true: '',
      false: ''
    },
    disabled: {
      true: 'pointer-events-none',
      false: ''
    }
  },
  compoundVariants: [{
    theme: 'light',
    isChecked: true,
    className: 'text-gray-900 before:border-gray-900'
  }, {
    theme: 'dark',
    isChecked: true,
    className: 'text-white before:border-white'
  }, {
    theme: 'light',
    isChecked: false,
    disabled: true,
    className: 'bg-gray-50 text-gray-300'
  }, {
    theme: 'dark',
    isChecked: false,
    disabled: true,
    className: 'bg-gray-700 text-gray-400'
  }, {
    theme: 'light',
    isChecked: false,
    disabled: false,
    className: 'hover:bg-silver-100 focus-visible:bg-silver-100 bg-white hover:text-gray-700 focus-visible:text-gray-700'
  }, {
    theme: 'dark',
    isChecked: false,
    disabled: false,
    className: 'bg-gray-800 text-gray-50 hover:bg-gray-700 hover:text-gray-50 focus-visible:bg-gray-700 focus-visible:text-gray-50'
  }],
  defaultVariants: {
    theme: 'light',
    isChecked: false,
    disabled: false
  }
});
export interface ListItem {
  uid: string;
  name: string;
  price?: number | null;
}
export interface ListSelectionProps {
  /**
   * (Optional) theme for the list selection
   */
  theme?: VariantProps<typeof ListSelectionStyled>['theme'];

  /**
   * (Optional) class name for the wrapper element
   */
  wrapperClassName?: string;

  /**
   * (Optional) class name for the container element
   */
  className?: string;

  /**
   * (Optional) ref for the items container element
   */
  itemsRef?: RefObject<HTMLButtonElement[]>;

  /**
   * list of items to render
   */
  items: ListItem[];

  /**
   * the value of the item
   */
  value: string;

  /**
   * (Optional) disabled state for the group list
   */
  disabled?: boolean;

  /**
   * (Optional) list of disabled by uid items
   */
  disabledItems?: string[];

  /**
   * (Optional) callback function to call when a value is selected
   */
  onSelected?: (item: ListItem, index: number) => void;

  /**
   * (Optional) tracking data for Google Analytics
   */
  dataGA?: Omit<DataGAProps, 'click'>;
}
type ListSelectionDataKeys = ExtendDataKeys<'selected'>;
export const ListSelection = t0 => {
  const $ = _c(24);
  let className;
  let dataGA;
  let itemsRef;
  let onSelected;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let value;
  let wrapperClassName;
  if ($[0] !== t0) {
    ({
      wrapperClassName,
      className,
      itemsRef,
      theme: t1,
      items: t2,
      value,
      disabled: t3,
      disabledItems: t4,
      onSelected,
      dataGA,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = dataGA;
    $[3] = itemsRef;
    $[4] = onSelected;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = value;
    $[11] = wrapperClassName;
  } else {
    className = $[1];
    dataGA = $[2];
    itemsRef = $[3];
    onSelected = $[4];
    props = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    value = $[10];
    wrapperClassName = $[11];
  }
  const theme = t1 === undefined ? "light" : t1;
  const items = t2 === undefined ? [] : t2;
  const disabled = t3 === undefined ? false : t3;
  const disabledItems = t4 === undefined ? [] : t4;
  const listSelectionRef = useRef([]);
  let t5;
  if ($[12] !== onSelected) {
    t5 = (item, index) => {
      safeCall(onSelected, item, index);
    };
    $[12] = onSelected;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleSelected = t5;
  const handleOnKeyDown = index_0 => event => {
    if (!listSelectionRef.current) {
      return;
    }
    if (["ArrowDown", "ArrowUp"].includes(event.nativeEvent.code)) {
      event.preventDefault();
      const elements = listSelectionRef.current.filter(_temp);
      if (elements.length === 0) {
        return;
      }
      const nextIndex = index_0 + 1 >= elements.length ? 0 : index_0 + 1;
      const prevIndex = index_0 - 1 < 0 ? elements.length - 1 : index_0 - 1;
      if (event.nativeEvent.code === "ArrowDown") {
        elements[nextIndex].focus();
        elements[nextIndex].click();
      }
      if (event.nativeEvent.code === "ArrowUp") {
        elements[prevIndex].focus();
        elements[prevIndex].click();
      }
    }
  };
  let t6;
  if ($[14] !== itemsRef) {
    t6 = [itemsRef];
    $[14] = itemsRef;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  useEffect(() => {
    if (!listSelectionRef.current || !itemsRef || itemsRef.current.length > 0) {
      return;
    }
    itemsRef.current = listSelectionRef.current;
  }, t6);
  if (!items || items.length === 0) {
    return null;
  }
  const t7 = "radiogroup";
  let t8;
  if ($[16] !== wrapperClassName) {
    t8 = cn("space-y-2", wrapperClassName);
    $[16] = wrapperClassName;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const t9 = "list-selection-label";
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = createDataAttributes({
      test: "list-selection"
    });
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = items.map((item_0, index_1) => {
    const isChecked = item_0.uid === value;
    const isValidPrice = item_0.price !== undefined && item_0.price !== null;
    const price = formatPrice(item_0?.price ?? 0);
    const isDisabled = disabled || disabledItems.length > 0 && disabledItems.includes(item_0.uid);
    return <button key={item_0.uid} type="button" role="radio" id={`radio_${item_0.uid}`} name="list-selection" value={item_0.uid} aria-checked={isChecked} aria-disabled={isDisabled} disabled={isDisabled} onClick={() => handleSelected(item_0, index_1)} onKeyDown={handleOnKeyDown(index_1)} className={ListSelectionStyled({
      theme,
      isChecked,
      disabled: isDisabled,
      className: !isDisabled || isChecked ? className : undefined
    })} ref={ref_0 => {
      if (!listSelectionRef.current) {
        return;
      }
      listSelectionRef.current[index_1] = ref_0 as HTMLButtonElement;
    }} {...createDataAttributes({
      test: `list-selection-${index_1}`,
      selected: isChecked ? "true" : undefined
    })} {...createDataGA({
      click: true,
      value: `${item_0?.name}${isValidPrice ? ` (${price})` : ""}`,
      text: item_0?.name,
      ...dataGA
    })}><span className="pointer-events-none" {...createDataAttributes({
        test: "name"
      })}>{item_0?.name ?? "-"}</span>{!!isValidPrice && <span className="pointer-events-none whitespace-nowrap" {...createDataAttributes({
        test: "price"
      })}>{price}</span>}</button>;
  });
  let t12;
  if ($[19] !== props || $[20] !== t10 || $[21] !== t11 || $[22] !== t8) {
    t12 = <div role={t7} className={t8} aria-labelledby={t9} {...t10} {...props}>{t11}</div>;
    $[19] = props;
    $[20] = t10;
    $[21] = t11;
    $[22] = t8;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  return t12;
};
function _temp(ref) {
  return !!ref;
}